import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { Store, User, Phone, Mail, Building2, CheckCircle, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function BakeryRegistrationPage() {
  const [formData, setFormData] = useState({
    name: '',
    bakery_name: '',
    company_name: '',
    email: '',
    phone: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.name.trim()) {
      toast.error('الاسم مطلوب');
      return;
    }
    if (!formData.bakery_name.trim()) {
      toast.error('اسم الفرن/المخبز مطلوب');
      return;
    }
    if (!formData.phone.trim()) {
      toast.error('رقم الهاتف مطلوب');
      return;
    }
    
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/bakery-registrations`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.detail || 'حدث خطأ أثناء التسجيل');
      }
      
      setSubmitted(true);
      toast.success('تم إرسال طلبك بنجاح! سنتواصل معك قريباً');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md shadow-2xl border-0">
          <CardContent className="pt-10 pb-10 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">تم استلام طلبك!</h2>
            <p className="text-gray-600 mb-6">
              شكراً لاهتمامك بنظام إدارة المخبز. سيتواصل معك فريقنا خلال 24 ساعة.
            </p>
            <Link to="/login">
              <Button variant="outline" className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                العودة لتسجيل الدخول
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg shadow-2xl border-0">
        <CardHeader className="text-center pb-2 bg-gradient-to-r from-amber-500 to-orange-500 text-white rounded-t-lg">
          <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Store className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl">تسجيل مخبز جديد</CardTitle>
          <CardDescription className="text-amber-100">
            سجل الآن للحصول على نظام إدارة المخبز الخاص بك
          </CardDescription>
        </CardHeader>
        
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* الاسم الكامل */}
            <div className="space-y-2">
              <Label htmlFor="name" className="flex items-center gap-2 text-gray-700">
                <User className="w-4 h-4" />
                الاسم الكامل *
              </Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="أدخل اسمك الكامل"
                className="text-right"
                data-testid="registration-name-input"
              />
            </div>

            {/* اسم الفرن/المخبز */}
            <div className="space-y-2">
              <Label htmlFor="bakery_name" className="flex items-center gap-2 text-gray-700">
                <Store className="w-4 h-4" />
                اسم الفرن / المخبز *
              </Label>
              <Input
                id="bakery_name"
                name="bakery_name"
                value={formData.bakery_name}
                onChange={handleChange}
                placeholder="مثال: مخبز السعادة"
                className="text-right"
                data-testid="registration-bakery-name-input"
              />
            </div>

            {/* اسم الشركة */}
            <div className="space-y-2">
              <Label htmlFor="company_name" className="flex items-center gap-2 text-gray-700">
                <Building2 className="w-4 h-4" />
                اسم الشركة (اختياري)
              </Label>
              <Input
                id="company_name"
                name="company_name"
                value={formData.company_name}
                onChange={handleChange}
                placeholder="مثال: شركة المخابز المتحدة"
                className="text-right"
                data-testid="registration-company-name-input"
              />
            </div>

            {/* البريد الإلكتروني */}
            <div className="space-y-2">
              <Label htmlFor="email" className="flex items-center gap-2 text-gray-700">
                <Mail className="w-4 h-4" />
                البريد الإلكتروني (اختياري)
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="example@email.com"
                className="text-left"
                dir="ltr"
                data-testid="registration-email-input"
              />
            </div>

            {/* رقم الهاتف */}
            <div className="space-y-2">
              <Label htmlFor="phone" className="flex items-center gap-2 text-gray-700">
                <Phone className="w-4 h-4" />
                رقم الهاتف *
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+49 123 456 7890"
                className="text-left"
                dir="ltr"
                data-testid="registration-phone-input"
              />
            </div>

            <Button 
              type="submit" 
              className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white py-6 text-lg"
              disabled={loading}
              data-testid="registration-submit-btn"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                  جاري الإرسال...
                </span>
              ) : (
                'إرسال طلب التسجيل'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-500 text-sm mb-2">لديك حساب بالفعل؟</p>
            <Link to="/login" className="text-amber-600 hover:text-amber-700 font-medium">
              تسجيل الدخول
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
